// src/pages/FormEditor.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const GENDERS = ['کوڕ','کچ'];
const empty_child = () => ({
  child_name:'', gender:'کوڕ', birth_year:'', birth_month:'', birth_day:'',
  profession:'', monthly_amount:'', total_amount:'', notes:''
});

const Field = ({ label, children, required }) => (
  <div style={fstyles.field}>
    <label style={fstyles.label}>{label}{required && <span style={{color:'red'}}> *</span>}</label>
    {children}
  </div>
);

const Input = (props) => <input style={fstyles.input} {...props} />;

export default function FormEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, can } = useAuth();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [depts, setDepts] = useState([]);

  const [emp, setEmp] = useState({ upn:'', full_name:'', national_id:'', rank:'', position:'', department_id:'' });
  const [spouse, setSpouse] = useState({ name:'', national_id:'', dob:'', profession:'' });
  const [meta, setMeta] = useState({ ministry:'', directorate:'', treasury_date:'', admin_date:'', directorate_date:'' });
  const [children, setChildren] = useState([empty_child()]);
  const [formNumber, setFormNumber] = useState('');

  useEffect(() => {
    api.get('/departments').then(r => setDepts(r.data));
    if (!isNew) {
      api.get(`/forms/${id}`).then(r => {
        const d = r.data;
        setEmp({ upn:d.upn, full_name:d.employee_name, national_id:d.national_id, rank:d.rank||'', position:d.position||'', department_id:d.department_id });
        setSpouse({ name:d.spouse_name||'', national_id:d.spouse_national_id||'', dob:d.spouse_dob?.split('T')[0]||'', profession:d.spouse_profession||'' });
        setMeta({ ministry:d.ministry||'', directorate:d.directorate||'', treasury_date:d.treasury_date?.split('T')[0]||'', admin_date:d.admin_date?.split('T')[0]||'', directorate_date:d.directorate_date?.split('T')[0]||'' });
        setChildren(d.children.length ? d.children : [empty_child()]);
        setFormNumber(d.form_number);
        setLoading(false);
      });
    }
  }, [id, isNew]);

  const addChild = () => setChildren([...children, empty_child()]);
  const removeChild = (i) => setChildren(children.filter((_,idx) => idx !== i));
  const updateChild = (i, field, val) => {
    // Block Arabic/Kurdish numerals
    if (['birth_year','birth_month','birth_day','monthly_amount','total_amount'].includes(field)) {
      val = val.replace(/[٠-٩۰-۹]/g, '').replace(/[^0-9.]/g, '');
    }
    const updated = [...children];
    updated[i] = {...updated[i], [field]: val};
    setChildren(updated);
  };

  const validateNationalId = (v) => /^\d{11}$/.test(v);
  const validateUPN = (v) => /^\d{13}$/.test(v);

  const handleEmpChange = (field, val) => {
    if (field === 'national_id') val = val.replace(/[^0-9]/g,'').slice(0,11);
    if (field === 'upn') val = val.replace(/[^0-9]/g,'').slice(0,13);
    setEmp({...emp, [field]:val});
  };

  const handleSpouseNid = (val) => {
    val = val.replace(/[^0-9]/g,'').slice(0,11);
    setSpouse({...spouse, national_id:val});
  };

  const submit = async () => {
    if (!validateUPN(emp.upn)) return toast.error('UPN دەبێت ١٣ ژمارەی ئینگلیزی بێت');
    if (!validateNationalId(emp.national_id)) return toast.error('ژمارەی نیشتیمانی دەبێت ١١ ژمارەی ئینگلیزی بێت');
    if (spouse.national_id && !validateNationalId(spouse.national_id)) return toast.error('ژمارەی نیشتیمانی هاوسەر دەبێت ١١ ژمارەی ئینگلیزی بێت');
    if (!emp.full_name) return toast.error('ناوی فەرمانبەر پێویستە');

    setSaving(true);
    try {
      const payload = { employee:{...emp}, spouse, children, ...meta };
      if (isNew) {
        const { data } = await api.post('/forms', payload);
        toast.success('فۆرم بە سەرکەوتوویی تۆمارکرا!');
        nav(`/forms/${data.id}`);
      } else {
        await api.put(`/forms/${id}`, payload);
        toast.success('فۆرم نوێکرایەوە!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'هەڵەیەک ڕووی دا');
    } finally {
      setSaving(false);
    }
  };

  const printPDF = async (style) => {
    if (isNew) return toast.error('پێشتر فۆرمەکە بتۆمارە');
    const { data } = await api.get(`/export/pdf-html/${id}?style=${style}`);
    const win = window.open('', '_blank');
    win.document.write(data.html);
    win.document.close();
    win.onload = () => { win.print(); };
  };

  const downloadExcel = () => {
    if (isNew) return toast.error('پێشتر فۆرمەکە بتۆمارە');
    window.open(`/api/export/excel/${id}`, '_blank');
  };

  if (loading) return <div style={{padding:40,textAlign:'center'}}>چاوەڕوانبە...</div>;
  if (!can('create') && isNew) return <div style={{padding:40}}>مەرجی دەستوورت نیە</div>;

  return (
    <div style={styles.page}>
      {/* Top bar */}
      <div style={styles.topbar}>
        <div>
          <h1 style={styles.title}>{isNew ? '➕ فۆرمی نوێ' : `✏️ دەستکاری فۆرم`}</h1>
          {formNumber && <span style={styles.formNum}>{formNumber}</span>}
        </div>
        <div style={styles.actions}>
          {!isNew && <>
            <button onClick={() => printPDF('classic')} style={styles.btnGray}>🖨️ پرینتی کلاسیک</button>
            <button onClick={() => printPDF('modern')} style={styles.btnGray}>🖨️ پرینتی مۆدێرن</button>
            <button onClick={downloadExcel} style={styles.btnGreen}>⬇️ ئێکزڵ</button>
          </>}
          <button onClick={submit} disabled={saving} style={styles.btnBlue}>
            {saving ? 'تۆمارکردن...' : '💾 تۆمارکردن'}
          </button>
        </div>
      </div>

      {/* Employee Section */}
      <Section title="👤 زانیاری فەرمانبەر">
        <div style={styles.grid2}>
          <Field label="UPN (١٣ ژمارە)" required>
            <Input value={emp.upn} onChange={e => handleEmpChange('upn',e.target.value)}
              placeholder="1234567890123" maxLength={13}
              style={{...fstyles.input, borderColor: emp.upn && !validateUPN(emp.upn) ? '#e74c3c' : undefined}} />
            {emp.upn && !validateUPN(emp.upn) && <small style={{color:'#e74c3c'}}>دەبێت ١٣ ژمارەی ئینگلیزی بێت</small>}
          </Field>
          <Field label="ناوی سیاتی فەرمانبەر" required>
            <Input value={emp.full_name} onChange={e => setEmp({...emp, full_name:e.target.value})} placeholder="ناوی تەواو" />
          </Field>
          <Field label="ژمارەی نیشتیمانی (١١ ژمارە)" required>
            <Input value={emp.national_id} onChange={e => handleEmpChange('national_id',e.target.value)}
              placeholder="12345678901" maxLength={11}
              style={{...fstyles.input, borderColor: emp.national_id && !validateNationalId(emp.national_id) ? '#e74c3c' : undefined}} />
            {emp.national_id && !validateNationalId(emp.national_id) && <small style={{color:'#e74c3c'}}>دەبێت ١١ ژمارەی ئینگلیزی بێت</small>}
          </Field>
          <Field label="بەش">
            <select style={fstyles.input} value={emp.department_id} onChange={e => setEmp({...emp, department_id:e.target.value})}>
              <option value="">--- بەش هەڵبژێرە ---</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name_ku} ({d.code})</option>)}
            </select>
          </Field>
          <Field label="پیشەی مێسرد/ژن">
            <Input value={emp.rank} onChange={e => setEmp({...emp, rank:e.target.value})} placeholder="پیشە" />
          </Field>
          <Field label="بواری کارکردن">
            <Input value={emp.position} onChange={e => setEmp({...emp, position:e.target.value})} placeholder="بواری کار" />
          </Field>
        </div>
      </Section>

      {/* Meta */}
      <Section title="🏛️ زانیاری وەزارەت و فەرمانگە">
        <div style={styles.grid3}>
          <Field label="وەزارەت">
            <Input value={meta.ministry} onChange={e => setMeta({...meta,ministry:e.target.value})} />
          </Field>
          <Field label="بەرێوەبەرایەتی">
            <Input value={meta.directorate} onChange={e => setMeta({...meta,directorate:e.target.value})} />
          </Field>
        </div>
        <div style={styles.grid3}>
          <Field label="بەروار - بێرەی پیاو/ژن (yyyy/mm/dd)">
            <Input type="date" value={meta.directorate_date} onChange={e => setMeta({...meta,directorate_date:e.target.value})} />
          </Field>
          <Field label="بەروار - تەلاقدراوە (yyyy/mm/dd)">
            <Input type="date" value={meta.admin_date} onChange={e => setMeta({...meta,admin_date:e.target.value})} />
          </Field>
          <Field label="بەروار - ولاتنامەی ژن/میرد (yyyy/mm/dd)">
            <Input type="date" value={meta.treasury_date} onChange={e => setMeta({...meta,treasury_date:e.target.value})} />
          </Field>
        </div>
      </Section>

      {/* Spouse */}
      <Section title="💍 زانیاری هاوسەر">
        <div style={styles.grid2}>
          <Field label="ناوی هاوسەر">
            <Input value={spouse.name} onChange={e => setSpouse({...spouse,name:e.target.value})} placeholder="ناوی تەواوی هاوسەر" />
          </Field>
          <Field label="ژمارەی نیشتیمانی هاوسەر (١١ ژمارە)">
            <Input value={spouse.national_id} onChange={e => handleSpouseNid(e.target.value)}
              placeholder="12345678901" maxLength={11}
              style={{...fstyles.input, borderColor: spouse.national_id && !validateNationalId(spouse.national_id) ? '#e74c3c':undefined}} />
          </Field>
          <Field label="بەروار لەدایکبوون">
            <Input type="date" value={spouse.dob} onChange={e => setSpouse({...spouse,dob:e.target.value})} />
          </Field>
          <Field label="پیشەی هاوسەر">
            <Input value={spouse.profession} onChange={e => setSpouse({...spouse,profession:e.target.value})} />
          </Field>
        </div>
      </Section>

      {/* Children */}
      <Section title="👶 منداڵەکان">
        <div style={{overflowX:'auto'}}>
          <table style={ctbl.table}>
            <thead>
              <tr style={ctbl.head}>
                <th style={ctbl.th}>#</th>
                <th style={ctbl.th}>ناوی منداڵ</th>
                <th style={ctbl.th}>رەگەز</th>
                <th style={ctbl.th}>رۆژ</th>
                <th style={ctbl.th}>مانگ</th>
                <th style={ctbl.th}>ساڵ</th>
                <th style={ctbl.th}>پیشە/فۆناغ</th>
                <th style={ctbl.th}>هەقە بر (دیناری)</th>
                <th style={ctbl.th}>کۆی پارە</th>
                <th style={ctbl.th}>ئامرازەکان</th>
              </tr>
            </thead>
            <tbody>
              {children.map((ch, i) => (
                <tr key={i} style={{background: i%2===0?'#fff':'#f8f9fa'}}>
                  <td style={ctbl.td}>{i+1}</td>
                  <td style={ctbl.td}>
                    <input style={ctbl.inp} value={ch.child_name}
                      onChange={e => updateChild(i,'child_name',e.target.value)} placeholder="ناو" />
                  </td>
                  <td style={ctbl.td}>
                    <select style={ctbl.inp} value={ch.gender} onChange={e => updateChild(i,'gender',e.target.value)}>
                      {GENDERS.map(g => <option key={g}>{g}</option>)}
                    </select>
                  </td>
                  <td style={ctbl.td}>
                    <input style={{...ctbl.inp,width:50}} value={ch.birth_day}
                      onChange={e => updateChild(i,'birth_day',e.target.value)} placeholder="DD" maxLength={2} />
                  </td>
                  <td style={ctbl.td}>
                    <input style={{...ctbl.inp,width:50}} value={ch.birth_month}
                      onChange={e => updateChild(i,'birth_month',e.target.value)} placeholder="MM" maxLength={2} />
                  </td>
                  <td style={ctbl.td}>
                    <input style={{...ctbl.inp,width:70}} value={ch.birth_year}
                      onChange={e => updateChild(i,'birth_year',e.target.value)} placeholder="YYYY" maxLength={4} />
                  </td>
                  <td style={ctbl.td}>
                    <input style={ctbl.inp} value={ch.profession}
                      onChange={e => updateChild(i,'profession',e.target.value)} placeholder="پیشە" />
                  </td>
                  <td style={ctbl.td}>
                    <input style={{...ctbl.inp,width:90}} value={ch.monthly_amount}
                      onChange={e => updateChild(i,'monthly_amount',e.target.value)} placeholder="0" />
                  </td>
                  <td style={ctbl.td}>
                    <input style={{...ctbl.inp,width:90}} value={ch.total_amount}
                      onChange={e => updateChild(i,'total_amount',e.target.value)} placeholder="0" />
                  </td>
                  <td style={ctbl.td}>
                    <button onClick={() => removeChild(i)} style={ctbl.delBtn} title="سڕینەوە">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{marginTop:10, display:'flex', gap:8, alignItems:'center'}}>
          <button onClick={addChild} style={styles.addChildBtn}>+ منداڵ زیادبکە</button>
          <span style={{fontSize:12,color:'#888'}}>
            (کۆی منداڵ: {children.length} | تەنها ٤ منداڵ بۆ پارە حساب دەکرێت)
          </span>
        </div>
      </Section>

      {/* Bottom save */}
      <div style={styles.bottomSave}>
        <button onClick={submit} disabled={saving} style={styles.btnBlue}>
          {saving ? 'تۆمارکردن...' : '💾 تۆمارکردن'}
        </button>
        <button onClick={() => nav('/forms')} style={styles.btnGray}>❌ داخستن</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={sec.box}>
      <h2 style={sec.title}>{title}</h2>
      {children}
    </div>
  );
}

const styles = {
  page: { padding:20 },
  topbar: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 },
  title: { fontSize:20, fontWeight:'bold', color:'#1a5276', margin:0 },
  formNum: { fontSize:12, background:'#eaf2fb', color:'#1a5276', borderRadius:10, padding:'2px 10px' },
  actions: { display:'flex', gap:8, flexWrap:'wrap' },
  grid2: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:12 },
  grid3: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, marginTop:12 },
  addChildBtn: { background:'#1e8449', color:'#fff', border:'none', borderRadius:6, padding:'8px 16px', cursor:'pointer', fontSize:13 },
  btnBlue: { background:'#1a5276', color:'#fff', border:'none', borderRadius:6, padding:'9px 18px', cursor:'pointer', fontSize:13, fontWeight:'bold' },
  btnGray: { background:'#f0f0f0', color:'#444', border:'1px solid #ddd', borderRadius:6, padding:'9px 18px', cursor:'pointer', fontSize:13 },
  btnGreen: { background:'#1e8449', color:'#fff', border:'none', borderRadius:6, padding:'9px 18px', cursor:'pointer', fontSize:13 },
  bottomSave: { display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 },
};
const sec = {
  box: { background:'#fff', borderRadius:10, padding:20, marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  title: { fontSize:14, fontWeight:'bold', color:'#1a5276', marginBottom:14, paddingBottom:6, borderBottom:'2px solid #eaf2fb' },
};
const fstyles = {
  field: { display:'flex', flexDirection:'column', gap:4 },
  label: { fontSize:12, fontWeight:'bold', color:'#555' },
  input: { padding:'8px 10px', border:'1.5px solid #ddd', borderRadius:6, fontSize:13, outline:'none', fontFamily:'inherit', transition:'border 0.2s' },
};
const ctbl = {
  table: { width:'100%', borderCollapse:'collapse', fontSize:12 },
  head: { background:'#1a5276' },
  th: { color:'#fff', padding:'8px 6px', textAlign:'center', fontSize:11, whiteSpace:'nowrap' },
  td: { padding:'4px', border:'1px solid #e0e0e0', textAlign:'center' },
  inp: { width:'100%', padding:'5px 4px', border:'1px solid #ddd', borderRadius:4, fontSize:12, textAlign:'center', fontFamily:'inherit', minWidth:60 },
  delBtn: { background:'none', border:'none', cursor:'pointer', fontSize:14 },
};
